import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Label,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  Button,
  Card,
  CardContent,
} from 'components/ui';
import { DatePicker } from '@app/components';
import { OkedSelectTrigger, KatoSelectTrigger } from '@app/components';
import {
  Kato,
  Measure,
  MeasureLabelsFull,
  Oked,
  Tender,
  TenderCategory,
  UserType,
} from '@app/models';
import * as yup from 'yup';
import { mapRecordToSelectOption, onlyDigit } from '@app/helpers';
import { useUserContext } from '@app/providers';
import { addDays } from 'date-fns';
import { useForm } from '../hooks';
import { useTranslation } from 'react-i18next';
import { IconClear, IconSendMessage } from '@app/icons';

interface Props {
  defaultExpanded: boolean;
  onSubmit: (data: TenderFormData) => void;
  tenderCategory: TenderCategory | null;
  tender?: Tender;
  setValues?: (values: Partial<TenderFormValues>) => void;
  edit?: boolean;
  errors?: any;
  data?: Partial<TenderFormData>;
}

export interface TenderFormValues {
  oked: Oked | null;
  kato: Kato | null;
  name: string;
  description: string;
  deliveryDate: Date | null;
  endDate: Date | null;
  price: string;
  measure: { label: string; value: number } | null;
}

export interface TenderFormData {
  oked: string | null;
  kato: string;
  name: string;
  description: string;
  deliveryDate: Date | null;
  endDate: Date | null;
  price: string;
  companyId: string | null;
  requirements?: string[];
  measure: number | null;
}

function TenderForm(props: Props) {
  const {
    setValues: setValuesProps,
    defaultExpanded,
    onSubmit,
    tenderCategory,
    tender,
    edit = false,
    errors: errorsProps,
    data,
  } = props;
  const { user, company } = useUserContext();
  const { t } = useTranslation();
  const [requirement, setRequirement] = useState<string>('');
  const [requirements, setRequirements] = useState<string[]>([]);
  const [expanded, setExpanded] = useState<boolean>(defaultExpanded);
  const { values, onChange, errors, validate, setValues, setErrors } =
    useForm<TenderFormValues>({
      oked: null,
      kato: null,
      name: data?.name ?? '',
      description: '',
      endDate: null,
      deliveryDate: null,
      price: data?.price ?? '',
      measure: null,
    });

  useEffect(() => {
    if (tender) {
      setValues({
        oked: tender.oked,
        kato: tender.kato,
        name: tender.name,
        description: tender.description,
        endDate: new Date(tender.endDate),
        deliveryDate: new Date(tender.deliveryDate),
        price: tender.price,
        measure: tender.measure
          ? {
              label: MeasureLabelsFull[tender.measure],
              value: +Measure[tender.measure],
            }
          : null,
      });
      if (tender?.requirements?.length > 0) {
        setRequirements(tender.requirements.map((elem) => elem.name));
      }
    }
  }, []);

  useEffect(() => {
    if (setValuesProps) {
      setValuesProps({ ...values });
    }
  }, [values]);

  useEffect(() => {
    if (errorsProps) {
      setErrors(errorsProps);
    }
  }, [errorsProps]);

  const onChangeSwitch = useCallback(() => {
    setExpanded(!expanded);
  }, [expanded]);

  const onChangeRequirement = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setRequirement(e.target.value);
    },
    []
  );

  const onClickAddRequirement = useCallback(() => {
    if (requirement.trim()) {
      setRequirements((prevRequirements) => [...prevRequirements, requirement]);
      setRequirement('');
    }
  }, [requirement]);

  const onClickDeleteRequirement = useCallback(
    (index: number) => () => {
      setRequirements((prevRequirements) =>
        prevRequirements.filter((_, i) => i !== index)
      );
    },
    []
  );

  const onChangeOked = useCallback(
    (data: { value: Oked }) => {
      onChange(data.value, 'oked');
    },
    [onChange]
  );

  const onChangeKato = useCallback(
    (data: { value: Kato }) => {
      onChange(data.value, 'kato');
    },
    [onChange]
  );

  const onChangePrice = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(onlyDigit(e.target.value), 'price');
    },
    [onChange]
  );

  const onClickSubmitForm = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      const hasErrors = await validate(
        yup.object().shape({
          oked: yup
            .object()
            .nullable()
            .required(t('required', { ns: 'validation' })!),
          kato: yup
            .object()
            .nullable()
            .required(t('required', { ns: 'validation' })!),
          name: yup.string().required(t('required', { ns: 'validation' })!),
          description: yup
            .string()
            .required(t('required', { ns: 'validation' })!),
          endDate: yup.date().required(t('required', { ns: 'validation' })!),
          deliveryDate: yup
            .date()
            .required(t('required', { ns: 'validation' })!),
          price:
            tenderCategory === TenderCategory.WORKER
              ? yup.string()
              : yup.string().required(t('required', { ns: 'validation' })!),
        })
      );
      if (hasErrors) return;
      onSubmit({
        ...values,
        oked: values.oked ? values.oked.code : null,
        kato: values.kato!.code,
        requirements,
        companyId: user.type === UserType.ENTITY ? company!.id : null,
        measure: values.measure && values.measure.value,
      });
      setExpanded(false);
    },
    [
      validate,
      tenderCategory,
      onSubmit,
      values,
      requirements,
      user.type,
      company,
      t,
    ]
  );

  const measureOptions = useMemo(() => {
    const measureArray = Object.values(Measure).filter((value) =>
      Number.isFinite(value)
    );
    return measureArray
      .map((measure) => mapRecordToSelectOption(measure, MeasureLabelsFull))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, []);

  const getParentNumber = useCallback(
    (category: TenderCategory | null): number | undefined => {
      const categoryToParentNumberMap: Record<TenderCategory, number> = {
        [TenderCategory.WORKER]: 60000001,
        [TenderCategory.SUPPLIER]: 70000001,
        [TenderCategory.VEHICLE]: 80000001,
        [TenderCategory.CONTRACTOR]: 90000001,
      };
      return category ? categoryToParentNumberMap[category] : undefined;
    },
    []
  );

  return (
    <Card className="p-6">
      <CardContent>
        <form>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-6">
            <div className="flex flex-col gap-2">
              <Label htmlFor="name">Наименование заявки</Label>
              <Input
                id="name"
                value={values.name}
                onChange={(e) => onChange(e.target.value, 'name')}
                name="name"
                placeholder="Введите наименование"
                required
              />
              {errors.name && (
                <span className="text-red-500 text-xs mt-1">{errors.name}</span>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="oked">
                {tenderCategory !== TenderCategory.WORKER
                  ? 'Выберите категорию'
                  : 'Выберите специализацию'}
              </Label>
              <OkedSelectTrigger
                value={values.oked ? values.oked.nameRu : ''}
                onChange={onChangeOked}
                helperText={errors.oked || undefined}
                error={!!errors.oked}
                parentNumber={getParentNumber(tenderCategory)}
              />
              {errors.oked && (
                <span className="text-red-500 text-xs mt-1">{errors.oked}</span>
              )}
            </div>
            {tenderCategory !== TenderCategory.WORKER && (
              <>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="price">Объем</Label>
                  <Input
                    id="price"
                    value={values.price}
                    onChange={onChangePrice}
                    name="price"
                    placeholder="Введите объем"
                    required
                  />
                  {errors.price && (
                    <span className="text-red-500 text-xs mt-1">
                      {errors.price}
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="measure">Единицы измерения</Label>
                  <Select
                    value={values.measure?.value?.toString() || ''}
                    onValueChange={(val) => {
                      const found = measureOptions.find(
                        (opt) => opt.value.toString() === val
                      );
                      onChange(found || null, 'measure');
                    }}
                  >
                    <SelectTrigger id="measure">
                      <SelectValue placeholder="Выберите ед. из." />
                    </SelectTrigger>
                    <SelectContent>
                      {measureOptions.map((option) => (
                        <SelectItem
                          key={option.value}
                          value={option.value.toString()}
                        >
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.measure && (
                    <span className="text-red-500 text-xs mt-1">
                      {errors.measure}
                    </span>
                  )}
                </div>
              </>
            )}
            <div className="flex flex-col gap-2">
              <Label htmlFor="kato">Выберите регион</Label>
              <KatoSelectTrigger
                value={values.kato ? values.kato.nameRu : ''}
                onChange={onChangeKato}
                helperText={errors.kato || undefined}
                error={!!errors.kato}
              />
              {errors.kato && (
                <span className="text-red-500 text-xs mt-1">{errors.kato}</span>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="deliveryDate">
                Срок поставки <span className="text-red-500">*</span>
              </Label>
              <DatePicker
                value={values.deliveryDate}
                name="deliveryDate"
                onChange={onChange}
                label=""
                minDate={new Date()}
                placeholderText={'Выберите дату'}
                required={false}
                error={!!errors.deliveryDate}
                helperText={errors.deliveryDate}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="endDate">
                СРОК ДЕЙСТВИЯ ЗАЯВКИ <span className="text-red-500">*</span>
              </Label>
              <DatePicker
                value={values.endDate}
                name="endDate"
                onChange={onChange}
                label=""
                placeholderText={'Выберите дату'}
                required={false}
                minDate={new Date()}
                maxDate={
                  tenderCategory === TenderCategory.WORKER
                    ? addDays(new Date(), 14)
                    : addDays(new Date(), 29)
                }
                error={!!errors.endDate}
                helperText={errors.endDate}
              />
            </div>
          </div>
          <div className="mt-6 flex flex-col gap-2">
            <Label htmlFor="description">Описание</Label>
            <Textarea
              id="description"
              value={values.description}
              onChange={(e) => onChange(e.target.value, 'description')}
              name="description"
              placeholder="Описание документа"
              required
              rows={3}
            />
            {errors.description && (
              <span className="text-red-500 text-xs mt-1">
                {errors.description}
              </span>
            )}
          </div>
          {!edit && expanded && (
            <Card className="mt-6">
              <CardContent>
                <div className="mb-4 flex flex-col gap-2">
                  <Label>Дополнительные требования</Label>
                  <div className="flex gap-2">
                    <Input
                      value={requirement}
                      onChange={onChangeRequirement}
                      placeholder="Введите наименование требования. Например, техосмотр не позднее октября"
                    />
                    <Button
                      type="button"
                      onClick={onClickAddRequirement}
                      size="icon"
                    >
                      <IconSendMessage />
                    </Button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {requirements.map((requirement, requirementIndex) => (
                    <div
                      key={requirementIndex.toString()}
                      className="bg-[#f5f5f5] rounded-[10px] h-12 flex gap-4 items-center px-4 box-border mt-2"
                    >
                      <p>{requirement}</p>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={onClickDeleteRequirement(requirementIndex)}
                      >
                        <IconClear className={'form-tender__clearBtn'} />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          <div className="flex justify-end pr-4 box-border mb-6 mt-6">
            <Button
              type="submit"
              className="smr-expandable-form__submit"
              onClick={onClickSubmitForm}
            >
              Продолжить
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

export default memo(TenderForm);
